/**
 * Contract — DeepDive AI: chats and their streamed answers.
 *
 * A chat is a document: a title, a star, and the turns asked in it. An answer
 * arrives as a stream of events — the reasoning steps while the agent thinks,
 * then the text a unit at a time, then the chart, the sources and the
 * follow-ups once the last word is in — and costs one AI credit (`credits()`
 * in ./bootstrap reflects it).
 *
 * This file is the backend spec for:
 *
 *   GET    /chats                                   → ChatList
 *   GET    /chats/:chatId                           → Chat (404 → null)
 *   POST   /chats               CreateChatRequest   → ChatSummary
 *   PATCH  /chats/:chatId       { title }           → ChatSummary
 *   PATCH  /chats/:chatId       { starred }         → ChatSummary
 *   DELETE /chats/:chatId                           → void
 *   PUT    /chats/:chatId/messages/:messageId/feedback { feedback } → void
 *   POST   /chats/:chatId/answers ChatAnswerRequest → text/event-stream of ChatEvent
 *
 * **Stop** is the client closing the stream (`ctx.signal` aborts). The server
 * keeps what it had sent: stopped while thinking, the answer is dropped (the
 * question stays); stopped mid-text, the message is persisted with
 * `status: 'stopped'` and the text that had arrived — no chart, sources or
 * follow-ups, which belong to a finished answer.
 *
 * **Regenerate** is the same stream with `kind: 'regenerate'`: the old answer
 * is removed once the stream is accepted and the new one takes its place in
 * the chat (a new message id) — phrased differently, same figures.
 */
import type { SourceCtx } from './context';
import type { SeriesKey, TimeSeries } from './primitives';

/** An ISO 8601 instant, e.g. `2026-09-14T23:00:00.000Z`. Chats need the hour, so not an `IsoDate`. */
export type IsoDateTime = string;

/**
 * `fast` answers in a sentence or two with one chart; `expert` breaks the
 * question into steps and adds the diagnosis. Same cost, same wait.
 */
export type ChatMode = 'fast' | 'expert';

export type ChatFeedback = 'positive' | 'negative';

/* ------------------------------------------------------------ answers */

export type ChatReasoningStep = { id: string; content: string };

/**
 * A source an answer cites; `[index]` markers in the text point at it. For the
 * app's own modules `url` is a canonical in-app path (`/brand-health`) — the
 * client applies its tree base — and `host` names the module.
 */
export type ChatCitation = { id: string; index: number; title: string; url: string; host?: string };

/** A question the answer invites. `label` is what is shown and what is asked. */
export type ChatFollowUp = { id: string; label: string };

/** A chart metric — a `MetricId` of `@/lib/analytics/metrics` (formats the axis and tooltip). */
export type ChatMetric =
  | 'mentions'
  | 'engagement'
  | 'reach'
  | 'npsNet'
  | 'share'
  /** A change between two periods, signed: bars may run below zero. */
  | 'percentChange'
  | 'difference';

/**
 * A category on a bar chart's axis:
 * - a filter-dimension option (`{ key: 'facebook', dimension: 'channel' }`) —
 *   the client labels it from `bootstrap.filterOptions`;
 * - a metric (`{ key: 'reactions' }`, no dimension) — "engagement by type";
 * - any other entity with its display label (`{ key, label }`) — a keyword.
 */
export type ChatChartCategory = SeriesKey | { key: string; label: string };

/**
 * The chart an answer carries. Raw values only; the client labels, colours and
 * formats it.
 *
 * - `donut` — slices in draw order. `center: 'total'` prints the slices' sum
 *   ("conversations"); `'own-share'` prints the workspace brand's share of the
 *   sum ("your share of voice").
 * - `bar` — categories in draw order (already ranked and cut by the server,
 *   since the text names them in that order). Series are metric keys, dimension
 *   options (a split), or the reserved key `value` — "each category's own
 *   total" on a metric-category chart. `values` align with `categories`.
 * - `line` — a time series (bucket starts, one or more series).
 */
export type ChatChart =
  | {
      kind: 'donut';
      metric: ChatMetric;
      center: 'total' | 'own-share';
      slices: (SeriesKey & { value: number })[];
    }
  | {
      kind: 'bar';
      metric: ChatMetric;
      orientation: 'horizontal' | 'vertical';
      stacked?: boolean;
      /** Each category drawn as 100%. */
      normalize?: boolean;
      categories: ChatChartCategory[];
      series: (SeriesKey & { values: number[] })[];
    }
  | { kind: 'line'; metric: ChatMetric; series: TimeSeries };

/** How much a recommended step asks of the team, as the engine rates it. */
export type ChatEffort = 'high' | 'medium' | 'low';

/** A next step the answer recommends. Both strings may carry inline Markdown (`**bold**`). */
export type ChatRecommendation = {
  recommendation: string;
  rationale: string;
  effort: ChatEffort | null;
};

/**
 * The figures behind an answer, as a grid: one row per entity, one column per
 * measure, every value one the engine traced to its data. Raw numbers; the
 * client labels the measures and formats the values by their unit.
 */
export type ChatFigures = {
  /** The entity every row belongs to (`Prime Bank`), or `''` for an account-wide breakdown. */
  owner: string;
  /** The window the figures cover, as the engine names it (`2026-08`, `all-time`). */
  scope: string;
  /** `measure` is the engine's key for it (`negativeVolume`); `unit` is `%`, `conversations`… */
  columns: { measure: string; unit: string }[];
  /** `values` align with `columns`; `null` where the entity has no figure for that measure. */
  rows: { name: string; url?: string; values: (number | null)[] }[];
};

/**
 * A figure the analyst placed in the answer: a chart or a table of figures,
 * drawn from the findings it names. `id` matches the `[[figure:<id>]]` line in
 * `ChatAnswer.text` that marks where it goes.
 */
export type ChatFigureBlock = {
  id: string;
  title?: string;
  chart?: ChatChart;
  figures?: ChatFigures;
};

export type ChatAnswer = {
  /**
   * The answer, as Markdown: one paragraph, or blocks — paragraphs, lists,
   * headings, quotes, GFM tables. `**bold**` runs and `[n]` citation markers
   * are the inline forms the client draws specially.
   */
  text: string;
  reasoning: ChatReasoningStep[];
  chart?: ChatChart;
  /** The figures behind the answer, when there are more than the chart can show. */
  figures?: ChatFigures;
  /** Figures the analyst placed in `text`, each at its `[[figure:<id>]]` line. */
  figureBlocks?: ChatFigureBlock[];
  /** Caveats about these figures, shown as footnotes. Inline Markdown. */
  notes?: string[];
  /** Next steps the answer recommends. */
  recommendations?: ChatRecommendation[];
  /** What this workspace's data cannot tell — true of every answer, so shown folded. */
  limitations?: string[];
  citations: ChatCitation[];
  followUps: ChatFollowUp[];
};

/* ----------------------------------------------------------- messages */

export type ChatUserMessage = { id: string; role: 'user'; text: string };

/**
 * A settled answer. `stopped` keeps only the text that had arrived, with no
 * chart, citations or follow-ups.
 */
export type ChatAssistantMessage = {
  id: string;
  role: 'assistant';
  status: 'done' | 'stopped';
  answer: ChatAnswer;
  feedback: ChatFeedback | null;
  /** The mode it was answered in. A `fast` answer offers to ask again in `expert`. */
  mode?: ChatMode;
};

export type ChatMessage = ChatUserMessage | ChatAssistantMessage;

/* -------------------------------------------------------------- chats */

export type ChatSummary = {
  id: string;
  /** The first prompt on one line, cut at a word (≤ 80 chars), until renamed. */
  title: string;
  createdAt: IsoDateTime;
  /** Starred chats sort first in the history. */
  starred: boolean;
};

export type Chat = ChatSummary & { messages: ChatMessage[] };

export type ChatList = {
  /** Every chat, in no particular order — the client sorts (starred first, then Newest/Oldest). */
  chats: ChatSummary[];
  /** The server's clock when listed; "1 hour ago" is measured against it. */
  asOf: IsoDateTime;
};

export type GetChatRequest = { chatId: string };
/** The server titles the chat from the prompt and picks its id (a slug of the title). */
export type CreateChatRequest = { question: string };
export type RenameChatRequest = { chatId: string; title: string };
export type StarChatRequest = { chatId: string; starred: boolean };
export type DeleteChatRequest = { chatId: string };
export type RateMessageRequest = {
  chatId: string;
  messageId: string;
  feedback: ChatFeedback | null;
};

/* ------------------------------------------------------------ streams */

export type ChatAnswerRequest =
  | { kind: 'ask'; chatId: string; question: string; mode: ChatMode }
  /** Ask the question behind `messageId` again, in that answer's mode. */
  | { kind: 'regenerate'; chatId: string; messageId: string };

/**
 * The abort reason when the chat is left, not stopped. The live source then
 * keeps the whole answer if it had already arrived, instead of the part shown.
 */
export const LEFT_CHAT = 'left-chat';

/**
 * One event of an answer stream, in this order:
 *
 *   accepted → reasoning* → answering → text* → chart? → citations → follow-ups → done
 *
 * `error` can end the stream instead of `done`. Pacing is the server's: the
 * mock thinks 6000ms, then sends a unit every 45ms, then waits 200ms before
 * the chart, citations, follow-ups and done arrive together.
 */
export type ChatEvent =
  /**
   * The question is persisted and the credit spent. `userMessage` is absent on
   * a regenerate; `replaces` is the answer being regenerated (now removed).
   */
  | { type: 'accepted'; messageId: string; userMessage?: ChatUserMessage; replaces?: string }
  | { type: 'reasoning'; step: ChatReasoningStep }
  /** Thinking is over; the text follows. */
  | { type: 'answering' }
  /** The next unit of text: a word, a whole `**bold run**` or a `[n]` marker, with its trailing whitespace. */
  | { type: 'text'; delta: string }
  | { type: 'chart'; chart: ChatChart }
  | { type: 'citations'; citations: ChatCitation[] }
  | { type: 'follow-ups'; followUps: ChatFollowUp[] }
  /** The answer as persisted. */
  | { type: 'done'; message: ChatAssistantMessage }
  | { type: 'error'; error: { code: string; message: string } };

export type ChatApi = {
  listChats(ctx: SourceCtx): Promise<ChatList>;
  /** `null` for an id nobody answers to (deleted, or a typed URL). */
  getChat(params: GetChatRequest, ctx: SourceCtx): Promise<Chat | null>;
  createChat(params: CreateChatRequest, ctx: SourceCtx): Promise<ChatSummary>;
  renameChat(params: RenameChatRequest, ctx: SourceCtx): Promise<ChatSummary>;
  starChat(params: StarChatRequest, ctx: SourceCtx): Promise<ChatSummary>;
  deleteChat(params: DeleteChatRequest, ctx: SourceCtx): Promise<void>;
  rateMessage(params: RateMessageRequest, ctx: SourceCtx): Promise<void>;
  /** Stop = abort `ctx.signal`. Costs one credit once `accepted`. */
  streamAnswer(request: ChatAnswerRequest, ctx: SourceCtx): AsyncIterable<ChatEvent>;
};
